import _ from 'lodash';
import axios from 'axios';
import { isObject } from '@skyjs/lodash-extras';
import outputFunctionCallInfo from './outputFunctionCallInfo.js';

export default function makeInvokeBackendFunction({ domainName, authToken }) {
  let verbose = false;

  const config = {
    baseURL: domainName.endsWith('.localhost')
      ? // dev: route through the current origin (incl. the vite port) so /api/ hits the proxy
        `http://${window.location.host}/api/dev`
      : `https://${domainName}/api/prod`,
    headers: { ...(authToken && { Authorization: authToken }) }
  };

  const api = axios.create(config);

  const invokeBackendFunction = async function invokeBackendFunction(functionName, params, method) {
    if (!_.isNil(params) && !isObject(params)) {
      // eslint-disable-next-line no-console
      console.error(`Invalid parameters for ${functionName}.`, params, method);
      params = undefined;
    }

    method = method || (params ? 'POST' : 'GET');
    let url = functionName;

    if (method === 'GET' && !_.isEmpty(params)) {
      const qs = Object.keys(params)
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join('&');
      url += '?' + qs;
    }

    const startTime = Date.now();

    const csrf = getCookie('csrf');
    const apiConfig = {
      ...config,
      ...(csrf && { headers: { 'X-CSRF-Token': csrf } })
    };

    try {
      const res =
        method === 'POST' ? await api.post(url, params, apiConfig) : await api.get(url, apiConfig);

      if (verbose) {
        const opts = { validateResult: true };
        outputFunctionCallInfo(functionName, params, res, Date.now() - startTime, opts);
      }

      const result = { data: res.data };

      if (res.headers['skyjs-deprecated-auth-token']) {
        result.deprecatedAuthTokenWarning = true;
      }

      return result;
    } catch (e) {
      throw (e.response && e.response.data) || e;
    }
  };

  invokeBackendFunction.setVerbose = isVerbose => {
    verbose = isVerbose;
  };

  invokeBackendFunction.updateAuthToken = newAuthToken => {
    if (newAuthToken == null) {
      delete api.defaults.headers.Authorization;
      delete config.headers.Authorization;
    } else {
      api.defaults.headers.Authorization = newAuthToken;
      config.headers.Authorization = newAuthToken;
    }
  };

  return invokeBackendFunction;
}

function getCookie(name) {
  const parts = `; ${window.document.cookie}`.split(`; ${name}=`);
  return parts.length === 2 ? parts[1].split(';')[0] : undefined;
}
